import { AdSlotResolver } from './ad-slot.resolver';
import { ApiClient } from '../../api';
import { PublicAdSettingsDto } from '../../dto/recs.dto';
import { AdSet } from '../../entities/ad-set';
import { AdSetSettings } from '../../entities/ad-set-settings';
import { PublicSettings } from '../../types';
import { defineOwnProperties } from '../../utils/object';
import { ApplicationContext } from '../context/application.context';
import { Injectable, Optional } from '../dependency-injection/decorators';
import { AdSetResolveEvent } from '../event-manager/event/ad-set-resolve.event';
import { Events } from '../event-manager/event/events';
import { EventManager } from '../event-manager/event-manager';
import { Logger } from '../logger/logger';

@Injectable()
export class AdSetResolver {
  public constructor (
    private readonly adSlotResolver: AdSlotResolver,
    private readonly apiClient: ApiClient,
    private readonly eventManager: EventManager,
    @Optional() private readonly logger?: Logger,
  ) {
  }

  public async resolve (context: ApplicationContext): Promise<AdSet | null> {
    const identifier = context.getApplicationId();
    this.logger?.info(`Resolving AdSet with identifier: ${identifier}`);

    let event = await this.eventManager.dispatch(
      Events.AD_SET_PRE_RESOLVE,
      new AdSetResolveEvent(identifier),
    );

    if (event.isPropagationStopped()) {
      return null;
    }

    let publicSettings: PublicSettings | null | undefined = context.getApplicationConfig().publicSettings;

    if (!publicSettings) {
      this.logger?.debug(`Fetching AdSet settings with identifier: ${identifier}`);
      try {
        publicSettings = await this.apiClient.getSettings(identifier);
      } catch (error) {
        this.logger?.error(`Error while resolving AdSet with identifier: ${identifier}`, error);

        return null;
      }
    }

    const adSet = new AdSet();
    defineOwnProperties<AdSet, { id: string }>(adSet, { id: publicSettings.adSet.identifier });

    const adSetSettings = new AdSetSettings();
    defineOwnProperties<AdSetSettings, PublicAdSettingsDto>(adSetSettings, publicSettings.adSet.settings);

    adSet.setSettings(adSetSettings);

    for (const adSlotConfig of publicSettings.adSlots) {
      const adSlot = await this.adSlotResolver.resolve(adSlotConfig);

      if (adSlot) {
        adSet.addAdSlot(adSlot);
      }
    }

    event = await this.eventManager.dispatch(Events.AD_SET_POST_RESOLVE, new AdSetResolveEvent(identifier, adSet));

    return event.getAdSet();
  }
}
